import { FileBlockDataType } from "types/FileBlockDataType";

import {
  formatDateDay,
  formatDateDayMonth,
  formatDateDayMonthYear,
  formatDateMonthShort,
  formatEventDate,
  nowDate,
  toDate,
  toDateInStockholmTimeZone,
} from "./dateHelpers";
import { getFileImageUrls } from "./imageHelpers/fileThumbnailImageHelper";
import { getUrlByLink } from "./pageHelpers/getUrlByLink";
import { getUrlFromUrlStructureBySlug } from "./pageHelpers/getUrlFromUrlStructureBySlug";
import { getFileSize, isNullOrEmpty } from "./string";
import { replaceImageVaultBasePath } from "./urlResolver";

const keyOf = (obj: any) => Object.keys(obj ? obj : {});

export const InterceptLinksCollection = (key, data, urlStructure) => {
  if (key === "linksCollection" && data[key]?.items?.length) {
    // Filter out links to null items, most commonly beacause whatever
    // the link links to hasn't been published, or don't have a parent
    // page that is within the overall site structure. Not good practice
    // to manipulate the data like this here but it's frickin' hard to
    // find and handle all the downstream usages that breaks.
    data[key].items = data[key].items.filter((x: unknown) => !!x);

    for (const linkItem of data[key].items) {
      const newUrl = getUrlByLink(linkItem, urlStructure);
      linkItem.url = newUrl;
    }
  }
};
export const InterceptRichText = (key, data, urlStructure) => {
  if (data[key]?.links?.entries?.hyperlink?.length) {
    for (const link of data[key]?.links?.entries?.hyperlink) {
      if (link?.globalLinkItem || link?.reference || link?.url) {
        link.url = getUrlByLink(link, urlStructure);
      } else if (link?.slug) {
        link.url = urlStructure?.urls?.filter((item) => item?.slug === link?.slug)[0]?.url || null;
      }
    }
  }
  if (data[key]?.links?.entries?.block?.length) {
    for (const block of data[key]?.links?.entries?.block) {
      if (block?.__typename?.toLowerCase() === "imageblock" && block?.link) {
        const newUrl = getUrlByLink(block?.link, urlStructure);
        block.link.url = newUrl;
      }
    }
  }
};
export const InterceptFaqListResult = (key, data, urlStructure) => {
  if (key === "faqListResult" && data[key]?.length) {
    for (const item of data[key]) {
      if (item?.answer?.links?.entries?.hyperlink?.length) {
        for (const link of item?.answer?.links?.entries?.hyperlink) {
          if (link?.slug) {
            link.url = urlStructure?.urls?.filter((item) => item?.slug === link?.slug)[0]?.url;
          }
        }
      }
    }
  }
};

export const InterceptPersonalizedClaimLinksCollection = (key, data, urlStructure) => {
  if (key === "personalizedClaimLinksCollection" && data[key]?.items?.length) {
    for (const linkItem of data[key].items) {
      if (!linkItem) continue;
      const newUrl = getUrlByLink(linkItem, urlStructure);
      linkItem.url = newUrl;
    }
  }
};

export const InterceptLinkObjectsAndSetUrl = (res, urlStructure) => {
  if (res && res?.data) {
    for (const aKey of keyOf(res.data)) {
      for (const as of keyOf(res.data[aKey])) {
        if (
          res.data[aKey]?.[as]?.["globalLinkItem"] !== undefined &&
          res.data[aKey]?.[as]?.["reference"] !== undefined &&
          res.data[aKey]?.[as]?.["url"] !== undefined
        ) {
          const newUrl = getUrlByLink(res.data[aKey]?.[as], urlStructure);
          res.data[aKey][as].url = newUrl;
        }
      }
    }
  }
};
export const InterceptFaqPage = (res, urlStructure) => {
  if (res) {
    if (res.globalLinkItem && res.reference && res.url) {
      const newUrl = getUrlByLink(res, urlStructure);
      res.url = newUrl;
    }
  }
};

export const InterceptInsurancePickerBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && typeName?.toLowerCase() === "insurancepickerblock") {
    if (res?.data?.insurancePickerBlock?.helpLink?.slug) {
      const url = getUrlFromUrlStructureBySlug(
        res?.data?.insurancePickerBlock?.helpLink?.slug,
        urlStructure,
      );
      if (url?.length > 0) res.data.insurancePickerBlock.helpLinkUrl = url;
    }
  }
};

export const InterceptVideoBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && typeName?.toLowerCase() === "videoblock") {
    if (res?.data?.videoblock?.linkToPage?.slug) {
      const url = getUrlFromUrlStructureBySlug(
        res.data?.videoblock?.linkToPage?.slug,
        urlStructure,
      );
      if (url?.length > 0) res.data.videoblock.externalLink = url;
    }
  }
};
export const InterceptImageBlock = (res, typeName) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "imageblock") {
    if (res?.data?.imageBlock?.ivImage?.[0]) {
      res.data.imageBlock.ivImage[0].MediaConversions = null;
      res.data.imageBlock.ivImage[0].Categories = null;
    }
  }
};
export const InterceptQuoteBlock = (res, typeName) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "quoteblock") {
    if (res?.data?.quoteBlock?.ivImage?.[0]) {
      res.data.quoteBlock.ivImage[0].MediaConversions = null;
      res.data.quoteBlock.ivImage[0].Categories = null;
    }
  }
};
export const InterceptLinkBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && res?.data?.link && typeName?.toLowerCase() === "link") {
    const newUrl = getUrlByLink(res.data?.link, urlStructure);
    res.data.link.url = newUrl;
  }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const InterceptTeaserBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "teaserblock") {
    if (res?.data?.teaserBlock?.ivImage?.[0]) {
      res.data.teaserBlock.ivImage[0].MediaConversions = null;
      res.data.teaserBlock.ivImage[0].Categories = null;
    }

    //If a global link is  set, then set the teaser link to the global link
    if (res.data?.teaserBlock?.globalLinkItem?.value) {
      res.data.teaserBlock.teaserBlockUrl = res.data.teaserBlock.globalLinkItem?.value;
    }
    //otherwise check if an internal link has been set
    else if (res.data?.teaserBlock?.link?.slug) {
      const url = getUrlFromUrlStructureBySlug(res.data?.teaserBlock?.link?.slug, urlStructure);

      if (url?.length > 0) {
        res.data.teaserBlock.teaserBlockUrl = url;
      }
    } else if (res.data?.teaserBlock?.link?.imageVaultFile) {
      const url = res.data?.teaserBlock?.link?.imageVaultFile[0]?.mediaUrl;
      const fileSize = getFileSize(res.data?.teaserBlock?.link?.imageVaultFile[0]?.fileSize);
      const filename = res.data?.teaserBlock?.link?.imageVaultFile[0]?.Name;
      const fileExtension = res.data?.teaserBlock?.link?.imageVaultFile[0]?.fileExtension;

      if (url?.length > 0) {
        res.data.teaserBlock.teaserBlockUrl = url;
      }
      if (fileSize) {
        res.data.teaserBlock.fileSize = fileSize;
      }
      if (filename) {
        res.data.teaserBlock.fileName = filename;
      }
      if (fileExtension) {
        res.data.teaserBlock.fileExtension = fileExtension;
      }
      res.data.teaserBlock.link.imageVaultFile[0].MediaConversions = [];
      res.data.teaserBlock.link.imageVaultFile[0].Categories = [];
      res.data.teaserBlock.link.imageVaultFile[0].Metadata = null;
      res.data.teaserBlock.link.imageVaultFile[0].Size180 = null;
      res.data.teaserBlock.link.imageVaultFile[0].Size240 = null;
      res.data.teaserBlock.link.imageVaultFile[0].Size375 = null;
      res.data.teaserBlock.link.imageVaultFile[0].Size480 = null;
      res.data.teaserBlock.link.imageVaultFile[0].Metadata = null;
    }
    //otherwise check if there is an external link to the teaser.
    else if (res?.data?.teaserBlock?.externalLink) {
      res.data.teaserBlock.teaserBlockUrl = replaceImageVaultBasePath(
        res.data.teaserBlock.externalLink,
      );
    }
    //Finally set the link to empty
    else {
      res.data.teaserBlock.teaserBlockUrl = "";
    }
  }
};

export const InterceptPageBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "page") {
    if (res.data?.page?.slug) {
      const url = getUrlFromUrlStructureBySlug(res.data?.page?.slug, urlStructure);

      if (url?.length > 0) {
        res.data.page.pageUrl = url;
      }
    }
    //Finally set the link to empty
    else {
      res.data.page.pageUrl = "";
    }

    if (res?.data?.page?.ivTeaserImage?.[0]?.MediaConversions) {
      res.data.page.ivTeaserImage[0].MediaConversions = [];
      res.data.page.ivTeaserImage[0].Categories = [];
    }
  }
};

export const InterceptFileBlock = (res, typeName) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "fileblock") {
    if (res.data?.fileBlock) {
      const fileBlock = res.data?.fileBlock;
      const file = fileBlock?.imageVaultFile && fileBlock?.imageVaultFile[0];
      const thumbnailImage = fileBlock?.ivThumbnail && fileBlock?.ivThumbnail[0];
      const metaData = fileBlock?.metaData;

      const urls = getFileImageUrls(thumbnailImage, file);

      res.data.fileBlock.fileData = {
        fileSize: getFileSize(metaData?.fileSize),
        fileUrl: file?.mediaUrl ? replaceImageVaultBasePath(file?.mediaUrl) : null,
        thumbNailAlt: thumbnailImage?.altText || file?.altText || null,
        transformedFileSize: metaData?.fileSize
          ? metaData?.fileSize.replace("MB", "Megabyte").replace("KB", "Kilobyte")
          : null,
        urls: urls,
        hasImage: Object.keys(urls).length ? true : false,
        hasExtraThumbnail: thumbnailImage?.mediaUrl ? true : false,
      } as FileBlockDataType;
      res.data.fileBlock.ivThumbnail = null;
      delete res.data.fileBlock.ivThumbnail;
      res.data.fileBlock.imageVaultFile = null;
      delete res.data.fileBlock.imageVaultFile;
    }
  }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const InterceptOrderBlock = (res, typeName) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "orderblock") {
    if (res?.data?.orderBlock?.mainAreaOrderCollection?.items?.length) {
      for (const item of res?.data?.orderBlock?.mainAreaOrderCollection?.items) {
        const file = item?.imageVaultFile && item?.imageVaultFile[0];
        const thumbnailImage = item?.ivThumbnail && item?.ivThumbnail[0];
        const metaData = item?.metaData;
        const urls = getFileImageUrls(thumbnailImage, file);

        item.fileData = {
          fileSize: getFileSize(metaData?.fileSize),
          fileUrl: file?.mediaUrl ? replaceImageVaultBasePath(file?.mediaUrl) : null,
          thumbNailAlt: thumbnailImage?.altText || file?.altText || null,
          transformedFileSize: metaData?.fileSize
            ? metaData?.fileSize.replace("MB", "Megabyte").replace("KB", "Kilobyte")
            : null,
          urls: urls,
          hasImage: Object.keys(urls).length ? true : false,
          hasExtraThumbnail: thumbnailImage?.mediaUrl ? true : false,
        } as FileBlockDataType;
        item.ivThumbnail = null;
        delete item.ivThumbnail;
        item.imageVaultFile = null;
        delete item.imageVaultFile;
      }
    }
  }
};

export const InterceptContactBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "contactblock") {
    // if an internal link has been set, then set that as contactBlockLink
    if (res.data?.contactBlock?.internalLink) {
      const url = getUrlFromUrlStructureBySlug(
        res.data?.contactBlock?.internalLink?.slug,
        urlStructure,
      );

      if (url?.length > 0) {
        res.data.contactBlock.contactBlockUrl = url;
      }
    }
    //otherwise check if there is an external link to the teaser.
    else if (res?.data?.contactBlock?.externalLink) {
      res.data.contactBlock.contactBlockUrl = res.data.contactBlock.externalLink;
    }
    //Finally set the link to empty
    else {
      res.data.contactBlock.contactBlockUrl = "";
    }
  }
};

export const InterceptPagesAndSetImages = (pageData) => {
  if (pageData && pageData.ivMainImage?.[0]) {
    if (pageData.ivMainImage?.[0]) {
      pageData.ivMainImage[0].MediaConversions = null;
      pageData.ivMainImage[0].Categories = null;
    }
  }
};

export const InterceptPublishDates = (pageData) => {
  if (pageData?.newspage?.publishDate) {
    pageData.newspage.publishDate = formatDateDayMonthYear(toDate(pageData?.newspage?.publishDate));
  }
  if (pageData?.eventpage?.publishDate) {
    const now = nowDate();

    const endDate = toDateInStockholmTimeZone(pageData?.eventpage?.endDate);
    const startDate = toDateInStockholmTimeZone(pageData?.eventpage?.startDate);

    const dateSignDay = formatDateDay(startDate);
    const dateSignMonth = formatDateMonthShort(startDate);

    const lastRegistrationDatePhysical = toDateInStockholmTimeZone(
      pageData?.eventpage?.endDateForRegistration,
    );

    const hasNoRegistrationDatesSet =
      isNullOrEmpty(pageData?.eventpage?.endDateForRegistration) &&
      isNullOrEmpty(pageData?.eventpage?.endDateForRegistrationRemote);

    const lastRegistrationDateRemote = toDateInStockholmTimeZone(
      pageData?.eventpage?.endDateForRegistrationRemote,
    );

    const lastRegistrationDatePhysicalString = formatDateDayMonth(lastRegistrationDatePhysical);
    const lastRegistrationDateRemoteString = formatDateDayMonth(lastRegistrationDateRemote);

    const physicalRegistrationIsOpen = lastRegistrationDatePhysical >= now;
    const remoteRegistrationIsOpen = lastRegistrationDateRemote >= now;

    const eventShortDate = [formatDateDayMonthYear(startDate)];
    const eventDates = [formatEventDate(startDate, endDate)];

    pageData.eventpage.dateSignDay = dateSignDay;
    pageData.eventpage.dateSignMonth = dateSignMonth;
    pageData.eventpage.isPastEvent = endDate < now;
    pageData.eventpage.hasNoRegistrationDatesSet = hasNoRegistrationDatesSet;

    pageData.eventpage.physicalRegistrationIsOpen = physicalRegistrationIsOpen;
    pageData.eventpage.remoteRegistrationIsOpen = remoteRegistrationIsOpen;
    pageData.eventpage.lastRegistrationDatePhysicalString = lastRegistrationDatePhysicalString;
    pageData.eventpage.lastRegistrationDateRemoteString = lastRegistrationDateRemoteString;
    pageData.eventpage.eventDatesStringArray = eventDates;
    pageData.eventpage.eventShortDate = eventShortDate;
  }
};

export const InterceptPagesAndSetUrlsAndNames = (pageData, pageName, urlStructure) => {
  if (pageData && pageData?.eventpage && pageData?.eventpage?.registrationLink) {
    const newUrl = getUrlByLink(pageData?.eventpage?.registrationLink, urlStructure);
    if (newUrl) pageData.eventpage.registrationLink.url = newUrl;
  }
  if (pageData && pageData?.eventpage && pageData?.eventpage?.registrationLinkTwo) {
    const newUrl = getUrlByLink(pageData?.eventpage?.registrationLinkTwo, urlStructure);
    if (newUrl) pageData.eventpage.registrationLinkTwo.url = newUrl;
  }
  if (
    pageData &&
    pageData?.insurancepage &&
    pageData?.insurancepage?.linkToInsuranceClaimPage?.slug
  ) {
    const newUrl = getUrlFromUrlStructureBySlug(
      pageData?.insurancepage?.linkToInsuranceClaimPage?.slug,
      urlStructure,
    );
    if (newUrl) pageData.insurancepage.urlToClaimPage = newUrl;
  }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const InterceptTeaserListBlock = (res, typeName, urlStructure) => {
  if (res && res?.data && res?.data && typeName?.toLowerCase() === "teaserlistblock") {
    if (res?.data?.teaserListBlock?.listItemsCollection?.items?.length) {
      for (const item of res?.data?.teaserListBlock?.listItemsCollection?.items) {
        if (item?.ivImage?.[0]) {
          item.ivImage[0].MediaConversions = null;
          item.ivImage[0].Categories = null;
        }

        //If a global link is  set, then set the teaser link to the global link
        if (item?.globalLinkItem && item?.globalLinkItem?.value) {
          item.teaserBlockUrl = item.globalLinkItem.value;
        }
        //otherwise check if an internal link has been set
        else if (item?.link?.slug) {
          const url = getUrlFromUrlStructureBySlug(item?.link?.slug, urlStructure);

          if (url?.length > 0) {
            item.teaserBlockUrl = url;
          }
        } else if (item?.link?.imageVaultFile) {
          const url = item?.link?.imageVaultFile[0]?.mediaUrl;
          const fileSize = getFileSize(item?.link?.imageVaultFile[0]?.fileSize);
          const filename = item?.link?.imageVaultFile[0]?.Name;
          const fileExtension = item?.link?.imageVaultFile[0]?.fileExtension;

          if (url?.length > 0) {
            item.teaserBlockUrl = url;
          }
          if (fileSize) {
            item.fileSize = fileSize;
          }
          if (filename) {
            item.fileName = filename;
          }
          if (fileExtension) {
            item.fileExtension = fileExtension;
          }
          item.link.imageVaultFile[0].MediaConversions = [];
          item.link.imageVaultFile[0].Categories = [];
          item.link.imageVaultFile[0].Metadata = null;
          item.link.imageVaultFile[0].Size180 = null;
          item.link.imageVaultFile[0].Size240 = null;
          item.link.imageVaultFile[0].Size375 = null;
          item.link.imageVaultFile[0].Size480 = null;
          item.link.imageVaultFile[0].Metadata = null;
        }
        //otherwise check if there is an external link to the teaser.
        else if (item?.externalLink) {
          item.teaserBlockUrl = replaceImageVaultBasePath(item.externalLink);
        }
        //Finally set the link to empty
        else {
          item.teaserBlockUrl = "";
        }
      }
    }
  }
};
